import { useRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { subirP12Api } from '@/utils/certificadosApi';

/** Tamaño máximo del .p12: el servidor admite 120000 caracteres base64 (90000 bytes). */
export const TAMANO_MAXIMO_P12 = 90000;

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  /** Se llama tras subir el certificado (el padre recarga la lista y avisa al resto de la app). */
  onSubido: () => void;
}

/** Lee un fichero y devuelve su contenido en base64 (sin el prefijo `data:`). */
function leerComoBase64(fichero: File): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onerror = () => rechazar(new Error('No se pudo leer el archivo'));
    lector.onload = () => resolver(String(lector.result).split(',')[1] ?? '');
    lector.readAsDataURL(fichero);
  });
}

const nombreSugerido = () => `FNMT ${new Date().getFullYear() + 4}`;

/** Asistente «Renovar»: sube el `.p12` del certificado nuevo, que queda preparado. */
export function AsistenteRenovar({ abierto, onCerrar, onSubido }: Props) {
  const { fetchWithAuth } = useUser();
  const [nombre, setNombre] = useState(nombreSugerido);
  const [fichero, setFichero] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pedirConfirmacionSinNif, setPedirConfirmacionSinNif] = useState(false);
  const [confirmaSinNif, setConfirmaSinNif] = useState(false);
  // Sesión (apertura del asistente) que lanzó la subida en vuelo; `null` si no hay ninguna.
  const [subiendoDesde, setSubiendoDesde] = useState<number | null>(null);
  const [sesionVisible, setSesionVisible] = useState(0);
  const enCurso = useRef(false);
  const sesion = useRef(0);

  /**
   * Cierra el asistente sin dejar en memoria la contraseña ni el fichero.
   * No toca `enCurso`: una subida ya lanzada sigue en vuelo y bloquea otra hasta que acabe.
   */
  const cerrar = () => {
    sesion.current += 1;
    setSesionVisible(sesion.current);
    setNombre(nombreSugerido());
    setFichero(null);
    setPassword('');
    setError(null);
    setPedirConfirmacionSinNif(false);
    setConfirmaSinNif(false);
    onCerrar();
  };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enCurso.current) return;
    if (!nombre.trim()) return setError('El nombre es obligatorio.');
    if (!fichero) return setError('Selecciona el archivo .p12 o .pfx.');
    if (fichero.size > TAMANO_MAXIMO_P12) {
      return setError('El fichero es demasiado grande para ser un certificado .p12.');
    }
    if (!password) return setError('Escribe la contraseña del certificado.');

    setError(null);
    enCurso.current = true;
    const miSesion = sesion.current;
    setSubiendoDesde(miSesion);
    try {
      let p12Data: string;
      try {
        p12Data = await leerComoBase64(fichero);
      } catch {
        if (sesion.current === miSesion) setError('No se pudo leer el archivo.');
        return;
      }
      const resultado = await subirP12Api(fetchWithAuth, {
        nombre: nombre.trim(),
        p12Data,
        password,
        ...(pedirConfirmacionSinNif && confirmaSinNif ? { confirmarSinNif: true } : {}),
      });
      const cerrado = sesion.current !== miSesion;
      if (resultado.ok) {
        onSubido();
        if (!cerrado) cerrar();
        return;
      }
      if (cerrado) return;
      const sinNif = resultado.codigo === 'SIN_NIF';
      setPedirConfirmacionSinNif(sinNif);
      if (!sinNif) setConfirmaSinNif(false);
      setError(resultado.error);
    } catch {
      if (sesion.current === miSesion) setError('No se pudo subir el certificado.');
    } finally {
      enCurso.current = false;
      setSubiendoDesde(null);
    }
  };

  const textoBoton =
    subiendoDesde === null ? (pedirConfirmacionSinNif ? 'Subir de todas formas' : 'Subir certificado') :
    subiendoDesde === sesionVisible ? 'Subiendo…' :
    'Terminando la subida anterior…';

  return (
    <Dialog open={abierto} onOpenChange={(abrir) => !abrir && cerrar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Renovar certificado</DialogTitle>
          <DialogDescription>
            Al pedir la renovación a la FNMT, el certificado se instala en tu navegador o en el llavero:
            expórtalo como .p12 con su clave privada y súbelo aquí. Quedará preparado hasta que lo actives.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={enviar} className="space-y-4">
          <div>
            <Label htmlFor="nombre-renovar">Nombre descriptivo</Label>
            <Input id="nombre-renovar" value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="p12-renovar">Archivo .p12 o .pfx</Label>
            <Input
              id="p12-renovar"
              type="file"
              accept=".p12,.pfx"
              onChange={(e) => {
                setFichero(e.target.files?.[0] ?? null);
                setPedirConfirmacionSinNif(false);
                setConfirmaSinNif(false);
              }}
            />
          </div>
          <div>
            <Label htmlFor="password-renovar">Contraseña del certificado</Label>
            <Input
              id="password-renovar"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {pedirConfirmacionSinNif && (
            <div className="flex items-start gap-2">
              <input
                id="confirma-sin-nif"
                type="checkbox"
                className="mt-1"
                checked={confirmaSinNif}
                onChange={(e) => setConfirmaSinNif(e.target.checked)}
              />
              <Label htmlFor="confirma-sin-nif">
                Este certificado no incluye el NIF del titular: confirmo que es mío
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={cerrar}>
              Cancelar
            </Button>
            <Button type="submit" disabled={subiendoDesde !== null || (pedirConfirmacionSinNif && !confirmaSinNif)}>
              {textoBoton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
